import {PropertyType} from 'common/report/constants';
import {DateRangeType} from 'common/widgets/date-range-picker/models/types';
import {
  BooleanOperator,
  FilterOperator,
  ListOperator,
  NumberOperator,
  ObjectOperator,
  StringOperator,
} from 'common/widgets/property-filter-menu/models/types';
import {DisplaySize, Placement, TooltipAttrs} from 'common/components/tooltip/types';

export type SelectedOperator = [FilterOperator, OperatorDescription];

export interface OperatorDescription {
  symbol: string;
  label: string;
  tooltip?: TooltipAttrs;
}

/**
 * Defines a symbol and description for each filter operator, on every property type
 * this list is sorted by usage metrics on p3 https://mixpanel.com/s/2ebosG
 */
export const PropertyTypeOperatorMap = new Map<PropertyType, {[key in FilterOperator]?: OperatorDescription}>([
  [
    PropertyType.Datetime,
    {
      [DateRangeType.RelativeAfter]: {
        symbol: `Last`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results within the last filter interval.`,
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.NotRelativeAfter]: {
        symbol: `Not in the last`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Excludes results within the last filter interval.`,
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.Between]: {
        symbol: `Between`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results within the filter range.`,
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.NotBetween]: {
        symbol: `Not between`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Excludes results within the filter range.`,
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.On]: {
        symbol: `On`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results from the filter date.`,
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.NotOn]: {
        symbol: `Not on`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Excludes results from the filter date.`,
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.RelativeBefore]: {
        symbol: `Before the last`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results from before the last filter interval.`,
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.Before]: {
        symbol: `Before`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results from before the filter date.`,
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.Since]: {
        symbol: `Since`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results since the filter date.`,
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.RelativeFuture]: {
        symbol: `In the next`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results that occur in the next filter interval.`,
          size: DisplaySize.Small,
        },
      },
    },
  ],
  [
    PropertyType.Object,
    {
      [ObjectOperator.Equals]: {symbol: `=`, label: `equals`},
      [ObjectOperator.Set]: {symbol: `{=}`, label: `is set`},
    },
  ],
  [
    PropertyType.List,
    {
      [ListOperator.Contains]: {symbol: `∋`, label: `contains`},
      [ListOperator.DoesNotContain]: {symbol: `∌`, label: `does not contain`},
    },
  ],
  [
    PropertyType.Number,
    {
      [NumberOperator.EqualTo]: {
        symbol: `Equals`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results that exactly match the filter value(s).`,
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.NotEqualTo]: {
        symbol: `Not equal`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Excludes results that exactly match the filter value(s).`,
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.GreaterThan]: {
        symbol: `Greater than`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results with values greater than the filter value(s).`,
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.AtLeast]: {
        symbol: `Greater than or equal to`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results with values greater than or equal to the filter value(s).`,
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.LessThan]: {
        symbol: `Less than`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results with values less than the filter value(s).`,
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.AtMost]: {
        symbol: `Less than or equal to`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results with values less than or equal to the filter value(s).`,
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.Between]: {
        symbol: `Between`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results with values within the filter range.`,
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.NotBetween]: {
        symbol: `Not between`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Excludes results with values within the filter range.`,
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.Set]: {
        symbol: `Is numeric`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results with a numeric value for the filter property.`,
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.NotSet]: {
        symbol: `Is not numeric`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Excludes only results with a numeric value for the filter property.`,
          size: DisplaySize.Small,
        },
      },
    },
  ],
  [
    PropertyType.Boolean,
    {
      [BooleanOperator.True]: {symbol: `is True`, label: null},
      [BooleanOperator.False]: {symbol: `is False`, label: null},
    },
  ],
  [
    PropertyType.String,
    {
      [StringOperator.Equals]: {
        symbol: `Is`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results that exactly match the filter value(s).`,
          size: DisplaySize.Small,
        },
      },
      [StringOperator.DoesNotEqual]: {
        symbol: `Is not`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Excludes results that exactly match the filter value(s).`,
          size: DisplaySize.Small,
        },
      },
      [StringOperator.Contains]: {
        symbol: `Contains`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results that partially match the filter value(s).`,
          size: DisplaySize.Small,
        },
      },
      [StringOperator.DoesNotContain]: {
        symbol: `Does not contain`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Excludes results that partially match the filter value(s).`,
          size: DisplaySize.Small,
        },
      },
      [StringOperator.Set]: {
        symbol: `Is set`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Includes only results with any value for the filter property.`,
          size: DisplaySize.Small,
        },
      },
      [StringOperator.NotSet]: {
        symbol: `Is not set`,
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: `Excludes results with any value for the filter property.`,
          size: DisplaySize.Small,
        },
      },
    },
  ],
]);
